import {
  Badge,
  LinkButton,
  Box,
  Inline,
  P,
  Stack,
  Grid,
  Text
} from "@adfenix/alexandria";
import { format } from "date-fns";
import styled from "styled-components";
import { ReadLayer } from "@adfenix/types";
import { getBadgeColorV2, getBadgeLightness } from "utils/badgeHelpers";
import UilPlay from "@iconscout/react-unicons/icons/uil-play-circle";
import { channelIcons } from "utils/channelIcons";
import { channelToCamelCase } from "utils/types";
import CalendarIcon from "static/svg/calendar";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Popover from "components/Popover";
import InfoCircle from "static/svg/info-circle";

interface Props {
  campaign: ReadLayer.CustomerCampaignSearchModel;
}

const StyledButtonIcon = styled.i`
  &::before {
    margin: 0;
  }
  color: #2552ec;
`;

const StyledDefaultIcon = styled.i`
  &::before {
    margin: 0;
  }
  color: #282e42;
`;

const StyledBadge = styled(Badge)`
  font-weight: 500;
  font-size: 12px;
  padding: 2px 4px;
`;

const StyledHeader = styled(Text)`
  font-weight: 500;
  font-size: 12px;
  color: #68718c;
`;

const StyledText = styled(Text)`
  font-weight: 500;
  font-size: 14px;
`;

const StyledGrid = styled(Grid)`
  column-gap: 24px;
  row-gap: 4px;
`;

export const CustomerCampaignRow = ({ campaign }: Props) => {
  const { t } = useTranslation();

  return (
    <Box
      p={"m"}
      display="flex"
      justifyContent="space-between"
      style={{ width: "100%" }}
    >
      <Stack spacing={"s"} data-tag="allowcampaignEvents">
        {/* row for campaign anme and subject type */}
        <Box>
          <P bold>
            {campaign.campaignTemplateName ||
              campaign.mergedBundleProduct?.name ||
              "Campaign name"}
            {campaign.subjectName && ` for ${campaign.subjectName}`}
          </P>
        </Box>

        <Inline alignItems="center" gap={"m"}>
          {campaign.start && (
            <>
              <StyledBadge
                lightness={getBadgeLightness(campaign.customerFacingStatus)}
                color={getBadgeColorV2(campaign.customerFacingStatus)}
              >
                {campaign.customerFacingStatus}
              </StyledBadge>

              {campaign.end && (
                <Inline alignItems="center" gap={"xs2"} noWrap>
                  <CalendarIcon />
                  <P color="grey" size="s">{`${format(
                    new Date(campaign.start),
                    "yyyy-MM-dd"
                  )} to ${format(new Date(campaign.end), "yyyy-MM-dd")}`}</P>
                </Inline>
              )}

              <Inline alignItems="center" gap="xs2" noWrap>
                <UilPlay size="20px" />
                {campaign?.bundle?.programName}
              </Inline>

              <Inline gap="xs2" noWrap>
                {campaign.channels.sort().map((channel, index) =>
                  channel === "Facebook" ||
                  channel === "Instagram" ||
                  campaign.customCampaignUrl ? (
                    <LinkButton
                      logLabel="campaign-link-button"
                      variant="text"
                      key={index}
                      target="_blank"
                      to={
                        (campaign.customCampaignUrl
                          ? campaign.customCampaignUrl
                          : channel === "Facebook" &&
                            campaign.facebookCampaignUrl
                          ? campaign?.facebookCampaignUrl
                          : campaign?.instagramCampaignUrl) ?? ""
                      }
                    >
                      <StyledButtonIcon
                        key={index}
                        className={`uil uil-${
                          channelIcons[channelToCamelCase(channel)]
                        } is-size-5 `}
                      />
                    </LinkButton>
                  ) : (
                    <StyledDefaultIcon
                      key={index}
                      className={`uil uil-${
                        channelIcons[channelToCamelCase(channel)]
                      } is-size-5`}
                    />
                  )
                )}
              </Inline>
            </>
          )}
        </Inline>
      </Stack>
      <Box display="flex">
        {campaign.channels.includes("DisplayAd") && (
          <Popover
            style={{
              display: "flex",
              width: "24px",
              marginRight: "26px",
              marginBottom: "-26px"
            }}
            trigger={<InfoCircle color="#2552EC" />}
          >
            <Box display="flex" flexDirection="column">
              <Text size="m" color="white">
                All data not available
              </Text>
              <br />
              <Text size="s" color="white">
                This channel doesn’t have all data points. We’re displaying all
                available.
              </Text>
            </Box>
          </Popover>
        )}

        <Stack spacing={"xs"}>
          {campaign.subjectType === "Property" &&
            campaign.type !== "Custom" && (
              <Box>
                <Link
                  to={`/customer/hq/${campaign.hq.id}/property/results/${campaign.property?.id}`}
                >
                  <Inline justifyContent="end" gap={"xs2"} alignItems="center">
                    {t("customer.campaign.viewReport.button.label")}
                    <StyledButtonIcon className="uil uil-arrow-right is-size-5" />
                  </Inline>
                </Link>
              </Box>
            )}

          <StyledGrid columns={"repeat(4,auto)"}>
            <StyledHeader>
              {t("customer.campaign.overview.impressions.label")}
            </StyledHeader>
            <StyledHeader>
              {t("customer.campaign.overview.reach.label")}
            </StyledHeader>
            <StyledHeader>
              {t("customer.campaign.overview.clicks.label")}
            </StyledHeader>
            <StyledHeader>
              {t("customer.campaign.overview.clickFrequency.label")}
            </StyledHeader>
            <StyledText>{campaign.statistics?.impressions ?? "-"}</StyledText>
            <StyledText>{campaign.statistics?.reach ?? "-"}</StyledText>
            <StyledText>{campaign.statistics?.clicks ?? "-"}</StyledText>
            <StyledText>
              {campaign.statistics?.clicks &&
              campaign.statistics?.clickFrequency
                ? Math.round(campaign.statistics.clickFrequency)
                : "- "}
              %
            </StyledText>
          </StyledGrid>
        </Stack>
      </Box>
    </Box>
  );
};
